import { TrackingEventTypes } from '@eagle/data-function-types';
import { EventLocationData, Undefinable } from '@eagle/react-common';
import L from 'leaflet';
import { DateTime } from 'luxon';
import {
  filterExcludedTripHistoryEvents as filterExcludedTripHistoryEventsOverride,
  isExcludedTripHistoryEvent,
} from './excluded-events';
import { toEventTime } from './trip-history-event-display-utils';

export interface TripSummaryLike {
  entityId: string;
  finish: DateTime;
  id: string;
  isOngoing: boolean;
  journeyId: string;
  start: DateTime;
}

const isFiniteNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value);
};

export { isExcludedTripHistoryEvent };

export const filterExcludedTripHistoryEvents = (events: EventLocationData[]): EventLocationData[] => {
  return filterExcludedTripHistoryEventsOverride(events);
};

export const getEventLatLng = (event?: EventLocationData): Undefinable<L.LatLng> => {
  const location = event?.data?.location;
  if (!location || !isFiniteNumber(location.latitude) || !isFiniteNumber(location.longitude)) return;
  return L.latLng(location.latitude, location.longitude);
};

export const isLocationUpdateEvent = (event: EventLocationData): boolean => {
  return event.eventTypeId === 'location-update' || event.eventTypeId === TrackingEventTypes.LOCATION_UPDATE;
};

export const getFiniteLocationEvents = (events: EventLocationData[]): EventLocationData[] => {
  return events.filter((event) => Boolean(getEventLatLng(event)));
};

export const getThingRangeBoundaryEvents = (events: EventLocationData[]): { end?: EventLocationData; start?: EventLocationData } => {
  const filteredEvents = filterExcludedTripHistoryEvents(events);
  const sorted = [...getFiniteLocationEvents(filteredEvents)].sort((first, second) => toEventTime(first.occurred).toMillis() - toEventTime(second.occurred).toMillis());
  return {
    end: sorted[sorted.length - 1],
    start: sorted[0],
  };
};

export const getThingRangeMapEvents = (events: EventLocationData[], showLocationUpdates: boolean): EventLocationData[] => {
  const filteredEvents = filterExcludedTripHistoryEvents(events);
  const { end, start } = getThingRangeBoundaryEvents(filteredEvents);
  const visibleEvents = [...filteredEvents]
    .filter((event) => {
      if (event.eventId === start?.eventId || event.eventId === end?.eventId) return true;
      return showLocationUpdates || !isLocationUpdateEvent(event);
    })
    .filter((event, index, list) => list.findIndex((item) => item.eventId === event.eventId) === index)
    .sort((first, second) => toEventTime(first.occurred).toMillis() - toEventTime(second.occurred).toMillis());

  return visibleEvents;
};

export const getTripEventsInRange = (trip: TripSummaryLike, events: EventLocationData[]): EventLocationData[] => {
  const tripEnd = trip.isOngoing ? DateTime.now() : trip.finish;

  return [...events]
    .filter((event) => {
      const eventTime = toEventTime(event.occurred);
      return eventTime >= trip.start && eventTime <= tripEnd;
    })
    .sort((first, second) => toEventTime(first.occurred).toMillis() - toEventTime(second.occurred).toMillis());
};

export const getVisibleTripTimelineEvents = (trip: Undefinable<TripSummaryLike>, events: EventLocationData[], showLocationUpdates: boolean): EventLocationData[] => {
  if (!trip) return [];

  const allTripEvents = filterExcludedTripHistoryEvents(getTripEventsInRange(trip, events));
  return allTripEvents
    .filter((_, index) => index > 0 && index < allTripEvents.length - 1)
    .filter((event) => showLocationUpdates || event.eventTypeId !== 'location-update');
};
